/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ComponentProps, Dispatch, useState } from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { When } from "react-if";
import { Box, theme, ModalConfirm, FlexContainer } from "@nordcloud/gnui";
import {
  ApplicationItemDescription,
  ApplicationItemTotals,
} from "~/components/ApplicationItem";
import { ExpandIcon } from "~/components/Styled";
import { useToggle } from "~/hooks";
import {
  isNotEmpty,
  generateActionConfirmText,
  handleError,
  toInputError,
} from "~/tools";
import { hasCo2e } from "~/tools/applicationMapper";
import { useRemoveApplication } from "~/views/applications/hooks";
import { EnvironmentList } from "./EnvironmentList";

type Props = {
  application: ComponentProps<typeof ApplicationItemTotals>["application"];
  editHandler?: Dispatch<boolean>;
  setEditAppId?: Dispatch<boolean>;
  getAppHandler?: (options: {
    variables: { id: string; year: string };
  }) => void;
};

export function ApplicationItem({
  application,
  editHandler,
  getAppHandler,
}: Props) {
  const [isExpanded, toggleIsExpanded] = useToggle();
  const [isModalConfirmOpen, setIsModalConfirmOpen] = useState(false);

  const { removeApplication } = useRemoveApplication({
    id: application.id,
    hasEnvs: isNotEmpty(application.envs ?? []),
    name: application.name,
    setIsModalConfirmOpen,
  });

  const onRemoveHandler = async () => {
    try {
      await removeApplication();
    } catch (error) {
      handleError(toInputError(error));
    } finally {
      setIsModalConfirmOpen(false);
    }
  };

  const showAppCo2eSavings = hasCo2e(application);
  const hasEnvironments = application.envsCount !== 0;

  return (
    <Box spacing="spacing02">
      <Row>
        <Col sm={5} md={4} lg={7}>
          <FlexContainer
            css={{ paddingLeft: hasEnvironments ? 0 : theme.spacing.spacing06 }}
          >
            <When condition={hasEnvironments}>
              <ExpandIcon
                name="chevronDown"
                title="chevronDown"
                isExpanded={isExpanded}
                css={{
                  marginRight: theme.spacing.spacing00,
                }}
                onClick={toggleIsExpanded}
              />
            </When>
            <ApplicationItemDescription
              isApplicationPage
              application={application}
            />
          </FlexContainer>
        </Col>
        <ApplicationItemTotals
          application={application}
          getAppHandler={getAppHandler}
          editHandler={editHandler}
          setIsModalConfirmOpen={setIsModalConfirmOpen}
          showAppCo2eSavings={showAppCo2eSavings}
        />
      </Row>
      <When condition={isExpanded}>
        <EnvironmentList
          environmentsCount={application.envsCount}
          applicationId={application.id}
          showAppCo2eSavings={showAppCo2eSavings}
        />
      </When>
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={onRemoveHandler}
        actionLabel="Delete"
        contentLabel="Delete Application"
        onClose={() => setIsModalConfirmOpen(false)}
      >
        {generateActionConfirmText(application.name)("delete")(
          "from Applications"
        )}
      </ModalConfirm>
    </Box>
  );
}
